'use client';

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotification } from '@/contexts/NotificationContext';
import {
  deleteTomVendingSiteVisitCash,
  listTomVendingSiteVisitCash,
  parseDollarsToCents,
  upsertTomVendingSiteVisitCash,
  type TomVendingSiteVisitCashRow,
} from '@/hooks/useTomVendingSiteVisitCash';
import { formatUsdFromCents } from '@/hooks/useTomVendingReports';
import type { TomVendingMachine } from '@/hooks/useTomVendingMachine';

const CASH_POLL_INTERVAL_MS = 20_000;

const TomsVendingSiteVisitCashSection: React.FC<{
  siteVisitId: number | null;
  visitStatus: string;
  machines: TomVendingMachine[];
}> = ({ siteVisitId, visitStatus, machines }) => {
  const { showError, showSuccess } = useNotification();
  const [rows, setRows] = useState<TomVendingSiteVisitCashRow[]>([]);
  const [granularity, setGranularity] = useState<'machine' | 'site' | null>(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'site' | 'machine'>('site');
  const [machineId, setMachineId] = useState<number | ''>('');
  const [billsInput, setBillsInput] = useState('');
  const [coinsInput, setCoinsInput] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const readOnly = visitStatus !== 'open' || siteVisitId == null;

  const reload = useCallback(async (opts?: { silent?: boolean }) => {
    if (siteVisitId == null) {
      setRows([]);
      setGranularity(null);
      return;
    }
    if (!opts?.silent) {
      setLoading(true);
    }
    try {
      const r = await listTomVendingSiteVisitCash(siteVisitId);
      setRows(r.rows);
      setGranularity(r.granularity);
      if (r.granularity === 'machine') setMode('machine');
      else if (r.granularity === 'site') setMode('site');
    } catch (e: unknown) {
      if (!opts?.silent) {
        showError(e instanceof Error ? e.message : 'Failed to load cash entries');
      }
      if (!opts?.silent) {
        setRows([]);
      }
    } finally {
      if (!opts?.silent) {
        setLoading(false);
      }
    }
  }, [siteVisitId, showError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (siteVisitId == null || visitStatus !== 'open') {
      return;
    }
    const timer = window.setInterval(() => {
      void reload({ silent: true });
    }, CASH_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [siteVisitId, visitStatus, reload]);

  const totalCollected = useMemo(
    () => rows.reduce((sum, r) => sum + r.totalCents, 0),
    [rows],
  );

  const resetForm = () => {
    setBillsInput('');
    setCoinsInput('');
    setNotes('');
    setMachineId('');
  };

  const save = async () => {
    if (siteVisitId == null) return;
    const billsCents = parseDollarsToCents(billsInput);
    const coinsCents = parseDollarsToCents(coinsInput);
    if (billsCents + coinsCents <= 0) {
      showError('Enter a bills or coins amount greater than zero');
      return;
    }
    if (mode === 'machine' && (machineId === '' || Number.isNaN(Number(machineId)))) {
      showError('Select a machine for per-machine cash entry');
      return;
    }
    setSaving(true);
    try {
      await upsertTomVendingSiteVisitCash({
        siteVisitId,
        vendingMachineId: mode === 'machine' ? Number(machineId) : null,
        billsCents,
        coinsCents,
        notes: notes.trim() || null,
      });
      showSuccess('Cash saved');
      resetForm();
      await reload();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to save cash');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: TomVendingSiteVisitCashRow) => {
    if (siteVisitId == null) return;
    try {
      await deleteTomVendingSiteVisitCash({
        siteVisitId,
        vendingMachineId: row.vendingMachineId,
      });
      await reload();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to remove cash entry');
    }
  };

  if (siteVisitId == null) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <AttachMoneyIcon color="action" />
        <Typography variant="subtitle1">Money collected</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          Total: {formatUsdFromCents(totalCollected)}
        </Typography>
      </Box>

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : rows.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          {rows.map((row) => (
            <Box
              key={`${row.vendingMachineId ?? 'site'}-${row.id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                {row.vendingMachineId != null
                  ? row.assetId?.trim() || `Machine #${row.vendingMachineId}`
                  : 'Site total'}
                {' · '}
                Bills {formatUsdFromCents(row.billsCents)} · Coins {formatUsdFromCents(row.coinsCents)}
                {' · '}
                <strong>{formatUsdFromCents(row.totalCents)}</strong>
                {row.notes ? ` · ${row.notes}` : ''}
              </Typography>
              {!readOnly ? (
                <IconButton size="small" aria-label="Remove cash entry" onClick={() => void remove(row)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No cash recorded for this visit yet. Amounts are shared totals per machine (latest save replaces prior values).
        </Typography>
      )}

      {!readOnly ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 140 }} disabled={granularity != null}>
            <InputLabel id="cash-entry-mode">Entry type</InputLabel>
            <Select
              labelId="cash-entry-mode"
              label="Entry type"
              value={mode}
              onChange={(e) => setMode(e.target.value as 'site' | 'machine')}
            >
              <MenuItem value="site">Site total</MenuItem>
              <MenuItem value="machine">Per machine</MenuItem>
            </Select>
          </FormControl>
          {mode === 'machine' ? (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="cash-machine">Machine</InputLabel>
              <Select
                labelId="cash-machine"
                label="Machine"
                value={machineId === '' ? '' : machineId}
                onChange={(e) => setMachineId(Number(e.target.value))}
              >
                {machines.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.assetId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          <TextField
            size="small"
            label="Bills ($)"
            value={billsInput}
            onChange={(e) => setBillsInput(e.target.value)}
            placeholder="0.00"
            sx={{ width: 120 }}
          />
          <TextField
            size="small"
            label="Coins ($)"
            value={coinsInput}
            onChange={(e) => setCoinsInput(e.target.value)}
            placeholder="0.00"
            sx={{ width: 120 }}
          />
          <TextField
            size="small"
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ minWidth: 160, flex: 1 }}
          />
          <Button variant="contained" size="small" disabled={saving} onClick={() => void save()}>
            Save cash
          </Button>
        </Box>
      ) : null}
    </Paper>
  );
};

export default TomsVendingSiteVisitCashSection;
